/**
 * The vocabulary of the tool state machine (§9.2).
 *
 * The machine is a **pure reducer**: `(machine, event, config) → result`.
 * It never touches the document, the DOM or the store. Instead it returns
 * *effects* — small, serialisable commands — which `AnnotationCanvas`
 * forwards to `editorStore`. That split is what makes the whole interaction
 * layer testable with synthetic events (`tools/__tests__/reducer.test.ts`)
 * and what keeps undo grouping honest: a gesture emits exactly one
 * `beginGesture` / `endGesture` pair.
 */
import type { Pt, Ring } from '@/geo/types'
import type { Facing, PortalEdge, Transition } from '@/schema'

export type ToolId = 'select' | 'polygon' | 'band' | 'rect' | 'horizon' | 'anchor' | 'portal' | 'path' | 'layer' | 'magnifier'

/** §9.2 exactly. `pan` and `pinch` are viewport gestures available under every tool. */
export type ToolState =
  | { t: 'idle' }
  | { t: 'polygon.drawing'; points: Pt[]; cursor: Pt }
  | { t: 'band.dragging'; y0: number; y1: number; kind: string }
  | { t: 'rect.dragging'; from: Pt; to: Pt }
  | { t: 'vertex.dragging'; id: string; index: number; start: Pt }
  | { t: 'feature.dragging'; ids: string[]; start: Pt; origin: Ring[] }
  | { t: 'marquee'; from: Pt; to: Pt; additive: boolean }
  | { t: 'horizon.dragging'; y: number }
  | { t: 'anchor.placing'; kind: string; at?: Pt; stand?: Pt; facing?: Facing }
  | { t: 'portal.linking'; from_id: string; cursor: Pt }
  | { t: 'path.drawing'; points: Pt[]; cursor: Pt }
  | { t: 'layer.baseline'; id: string; y: number }
  | { t: 'pan'; start: { x: number; y: number }; origin: { tx: number; ty: number } }
  | { t: 'pinch'; pointers: Map<number, { x: number; y: number }>; startK: number; startDist: number; startCenter: Pt }

export interface ToolMachine {
  tool: ToolId
  state: ToolState
}

export const IDLE: ToolMachine = { tool: 'select', state: { t: 'idle' } }

/** What the canvas found under the pointer; children stamp `data-id` / `data-vertex` / `data-edge`. */
export interface HitTarget {
  id?: string
  /** Vertex index of `id`'s ring. */
  vertex?: number
  /** Edge index (edge `i` joins vertex `i` and `i+1`) — dragging it inserts a vertex. */
  edge?: number
  /** The pointer is over the horizon line. */
  horizon?: boolean
  /** The pointer is over an occluder baseline handle. */
  baseline?: string
  /** The feature under the pointer is locked (a human owns it). */
  locked?: boolean
}

export interface PointerEventLike {
  /** Percent coordinates, already snapped by the canvas. */
  at: Pt
  /** Raw client pixels — pan and pinch work in screen space. */
  screen: { x: number; y: number }
  pointerId: number
  button: number
  shift: boolean
  alt: boolean
  /** `Ctrl` on Windows/Linux, `Meta` on macOS. */
  mod: boolean
  /** `true` for touch/pen, which widens the hit radii (§9.2: 14 px). */
  coarse: boolean
  hit: HitTarget
}

export type ToolEvent =
  | ({ t: 'pointerdown' } & PointerEventLike)
  | ({ t: 'pointermove' } & PointerEventLike)
  | ({ t: 'pointerup' } & PointerEventLike)
  | { t: 'pointercancel'; pointerId: number }
  | ({ t: 'dblclick' } & PointerEventLike)
  | { t: 'key'; key: string; shift: boolean; mod: boolean; repeat?: boolean }
  | { t: 'setTool'; tool: ToolId }
  | { t: 'cancel' }
  | { t: 'spaceDown' }
  | { t: 'spaceUp' }

/** Every mutation the machine can ask for. The store is the only thing that knows how to apply them. */
export type ToolEffect =
  /** Undo grouping: everything between these two lands in one history entry. */
  | { k: 'beginGesture' }
  | { k: 'endGesture' }
  | { k: 'cancelGesture' }
  | { k: 'select'; ids: string[]; additive?: boolean }
  | { k: 'selectVertex'; id: string; index: number }
  | { k: 'selectInRect'; from: Pt; to: Pt; additive: boolean }
  | { k: 'clearSelection' }
  | { k: 'createRegion'; ring: Ring; kind?: string }
  | { k: 'createBand'; y0: number; y1: number; kind?: string }
  | { k: 'moveVertex'; id: string; index: number; to: Pt }
  | { k: 'insertVertex'; id: string; index: number; at: Pt }
  | { k: 'deleteVertex'; id: string; index: number }
  /**
   * `fromStart`: `delta` is the TOTAL offset since the drag began and applies to the pre-drag
   * geometry (the store keeps it). Without it `delta` is added to the current geometry (a nudge).
   */
  | { k: 'translateFeatures'; ids: string[]; delta: Pt; fromStart?: boolean }
  | { k: 'setHorizon'; y: number }
  | { k: 'createAnchor'; kind: string; at: Pt; stand?: Pt; facing?: Facing }
  | { k: 'moveAnchor'; id: string; at: Pt }
  | { k: 'linkPortal'; from_id: string; to_id: string }
  | { k: 'createPath'; points: Pt[]; kind?: string }
  /** A way in/out: on a picture edge, or standing on a spot (`anchor_id`). One of the two is required. */
  | { k: 'createPortal'; kind?: string; edge?: PortalEdge; anchor_id?: string; transition?: Transition }
  | { k: 'setBaseline'; id: string; y: number }
  | { k: 'panTo'; tx: number; ty: number }
  | { k: 'zoomTo'; scale: number; centre: { x: number; y: number } }
  | { k: 'toast'; message: string; tone: 'info' | 'success' | 'warning' | 'error' }

export interface ToolResult {
  machine: ToolMachine
  effects: ToolEffect[]
}

/** Thresholds and live context a handler needs. All radii are percent, already divided by the zoom. */
export interface ToolConfig {
  /** Hit radius for picking a vertex. */
  vertexRadius: number
  /** Hit radius for picking an edge or the horizon. */
  edgeRadius: number
  /** How close the cursor must come to the first point to close a polygon. */
  closeRadius: number
  /** Minimum drag before a click becomes a drag. */
  dragThreshold: number
  /** Current viewport, for pan and pinch. */
  viewport: { k: number; tx: number; ty: number }
  /** Kind the drawing tools stamp on a new feature (from the kind legend / `1–9`). */
  activeKind: { region?: string; anchor?: string; portal?: string; path?: string }
  /** Ids currently selected — `select` needs them to decide between "start a drag" and "restart". */
  selection: string[]
  /** Feature id → outer ring, for feature drags. Only the selected ids need to be present. */
  ringsById: (id: string) => Ring | undefined
  /** Current `depth.horizon_y`, when the document has one. */
  horizonY?: number
  /** What a feature id is (from the document); lets the way-in/out tool tell a spot from a door marker. */
  typeOf?: (id: string) => FeatureType | undefined
  /** A feature's current kind. */
  kindOf?: (id: string) => string | undefined
}

export type FeatureType = 'region' | 'anchor' | 'portal' | 'path' | 'layer'

export const DEFAULT_TOOL_CONFIG: ToolConfig = {
  vertexRadius: 1,
  edgeRadius: 0.8,
  closeRadius: 1.4,
  dragThreshold: 0.3,
  viewport: { k: 1, tx: 0, ty: 0 },
  activeKind: {},
  selection: [],
  ringsById: () => undefined,
}

export interface ToolHandler {
  id: ToolId
  /** Letter shown in the rail and matched on `event.key` (§9.3). */
  shortcut: string
  /** Lucide icon name — `ToolRail` resolves it. */
  icon: string
  label: string
  /** One plain sentence under the icon; this is the whole onboarding. */
  hint: string
  cursor: string
  reduce(machine: ToolMachine, ev: ToolEvent, cfg: ToolConfig): ToolResult | null
}

/* -------------------------------------------------------------------------- */
/* Small shared helpers                                                        */
/* -------------------------------------------------------------------------- */

export const none = (machine: ToolMachine): ToolResult => ({ machine, effects: [] })

export const idle = (tool: ToolId, effects: ToolEffect[] = []): ToolResult => ({ machine: { tool, state: { t: 'idle' } }, effects })

export const withState = (tool: ToolId, state: ToolState, effects: ToolEffect[] = []): ToolResult => ({ machine: { tool, state }, effects })

export const distPct = (a: Pt, b: Pt): number => Math.hypot(a[0] - b[0], a[1] - b[1])
